import * as yup from 'yup';
import { ptShort } from 'yup-locale-pt';

declare module 'yup' {
    interface StringSchema {
        must: (fn: (value: string | undefined | null) => boolean, ErrorMessage?: string) => StringSchema,
        booleanString: (errorMessage?: string) => StringSchema,
        validCPF: (errorMessage?: string) => StringSchema,
        validCNPJ: (errorMessage?: string) => StringSchema,
        telefone: () => StringSchema,
        telefoneFixo: () => StringSchema,
        celular: () => StringSchema,
    }
}

const somenteDigitos = (value: string) => {
    return value.replace(/[^\d]+/g, '');
}

export const telefone = function (value: string | null | undefined) {
    if (value === undefined || value === null) {
        return true;
    }

    const numeroSemMascara = somenteDigitos(value);
    return numeroSemMascara.length === 10 || (numeroSemMascara.length === 11 && Number(numeroSemMascara[2]) === 9);
}

export const telefoneFixo = (value: string | null | undefined) => {
    if (value === undefined || value === null) {
        return true;
    }

    const numeroSemMascara = somenteDigitos(value);
    return numeroSemMascara.length === 10;
}

export const celular = (value: string | null | undefined) => {
    if (value === undefined || value === null) {
        return true;
    }

    const numeroSemMascara = value?.replace(/[^\d]+/g, '') || '';
    return numeroSemMascara.length === 11 && Number(numeroSemMascara[2]) === 9;
}

export const cep = (value: string | null | undefined) => {
    if (value === undefined || value === null) {
        return true;
    }

    const numeroSemMascara = somenteDigitos(value);
    return numeroSemMascara.length === 8;
}

export const validCPF = (cpf: string | null | undefined) => {
    if (cpf === undefined || cpf === null) {
        return true;
    }

    cpf = cpf.replace(/[^\d]+/g,'');

    if (cpf.length !== 11) {
        return false;
    }
    if ((cpf === '00000000000') || (cpf === '11111111111') || (cpf === '22222222222') || (cpf === '33333333333') || (cpf === '44444444444') || (cpf === '55555555555') || (cpf === '66666666666') || (cpf === '77777777777') || (cpf === '88888888888') || (cpf === '99999999999')) {
        return false;
    }
    let numero: number = 0;
    let caracter: string = '';
    let numeros: string = '0123456789';
    let j: number = 10;
    let somatorio: number = 0;
    let resto: number = 0;
    let digito1: number = 0;
    let digito2: number = 0;
    let cpfAux: string = '';
    cpfAux = cpf.substring(0, 9);
    for (let i: number = 0; i < 9; i++) {
        caracter = cpfAux.charAt(i);
        if (numeros.search(caracter) === -1) {
            return false;
        }
        numero = Number(caracter);
        somatorio = somatorio + (numero * j);
        j--;
    }
    resto = somatorio % 11;
    digito1 = 11 - resto;
    if (digito1 > 9) {
        digito1 = 0;
    }
    j = 11;
    somatorio = 0;
    cpfAux = cpfAux + digito1;
    for (let i: number = 0; i < 10; i++) {
        caracter = cpfAux.charAt(i);
        numero = Number(caracter);
        somatorio = somatorio + (numero * j);
        j--;
    }
    resto = somatorio % 11;
    digito2 = 11 - resto;
    if (digito2 > 9) {
        digito2 = 0;
    }
    cpfAux = cpfAux + digito2;
    if (cpf !== cpfAux) {
        return false;
    }
    else {
        return true;
    }        
}

export const validCNPJ = (cnpj: string | null | undefined) => {
    if (!cnpj) {
        return true;
    }

    cnpj = cnpj.replace(/[^\d]+/g,'');

    if(cnpj === '') return false;
     
    if (cnpj.length !== 14)
        return false;
 
    // Elimina CNPJs invalidos conhecidos
    if (cnpj === "00000000000000" || 
        cnpj === "11111111111111" || 
        cnpj === "22222222222222" || 
        cnpj === "33333333333333" || 
        cnpj === "44444444444444" || 
        cnpj === "55555555555555" || 
        cnpj === "66666666666666" || 
        cnpj === "77777777777777" || 
        cnpj === "88888888888888" || 
        cnpj === "99999999999999")
        return false;
         
    // Valida DVs
    let tamanho = cnpj.length - 2
    let numeros = cnpj.substring(0,tamanho);
    let digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += +numeros.charAt(tamanho - i) * pos--;
      if (pos < 2)
            pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
    if (resultado !== +digitos.charAt(0))
        return false;
         
    tamanho = tamanho + 1;
    numeros = cnpj.substring(0,tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += +numeros.charAt(tamanho - i) * pos--;
      if (pos < 2)
            pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
    if (resultado !== +digitos.charAt(1))
          return false;
           
    return true;
}

yup.setLocale(ptShort);

yup.addMethod<yup.StringSchema>(yup.string, "validCPF", function (errorMessage) {
    return this.test(`validCPF`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        validCPF(value) || createError({ path, message: errorMessage })
      );
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "validCNPJ", function (errorMessage) {
    return this.test(`validCNPJ`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        validCNPJ(value) || createError({ path, message: errorMessage })
      );
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "telefone", function (errorMessage) {
    return this.test(`telefone`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        telefone(value) || createError({ path, message: errorMessage })
      );
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "telefoneFixo", function (errorMessage) {
    return this.test(`telefoneFixo`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        telefoneFixo(value) || createError({ path, message: errorMessage })
      );
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "celular", function (errorMessage) {
    return this.test(`celular`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        celular(value) || createError({ path, message: errorMessage })
      );
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "cep", function (errorMessage) {
    return this.test(`cep`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        cep(value) || createError({ path, message: errorMessage })
      );
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "booleanString", function (errorMessage) {
    return this.test(`booleanString`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        value === "0" || value === "1" || createError({ path, message: errorMessage })
      );
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "must", function (fn: (value: string | undefined | null) => boolean, errorMessage: string) {
    return this.test(`must`, errorMessage, function (value) {
      const { path, createError } = this;
  
      return (
        fn(value) || createError({ path, message: errorMessage })
      );
    });
});

export {
    yup
};
